import { Box } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { PageContainer, GreyContainer, InnerContentContainer } from "../../components";
import { SquareCardLink, Button, CourseCardLoading } from "../../components/elements";
import EbooksService from "../../services/ebooks.service";
import EbooksCategoriesService from "../../services/ebooksCategories.service";

import { EbooksContainer, ViewMoreWrapper } from "./EbooksStyled";
import { Tag } from "../../components/elements/SquareCard/SquareCardStyled";
import { CategoriesContainer, FilterTitle } from "../Articles/ArticlesStyled";
import { useParams } from "react-router-dom";

import loadingGif from "../../assets/images/loading.gif";
import { HomePageFeatureSlider } from "../../components/features/HomePageFeatureSlider";
import { sanitizeHtml, sanitizeAndTruncate } from "../../utils/sanitize";
import { IEbook, IEbookCategory } from "../../types/ebooks.types";
import { FALLBACK_IMAGES } from "../../constants/images";

export interface Props {
    background?: string;
}

const Ebooks: React.FC<Props> = ({ ...props }) => {
    const [lastPage, setLastPage] = useState(false);
    const [ebooksNextPage, setEbooksNextPage] = useState(1);
    const [ebooks, setEbooks] = useState<IEbook[]>([]);
    const [ebooksCount, setEbooksCount] = useState(0);
    const [viewMoreDisabled, setViewMoreDisabled] = useState(false);
    const [loadingEbooks, setLoadingEbooks] = useState(true);
    const [ebookCategories, setEbookCategories] = useState<IEbookCategory[]>([]);
    const [error, setError] = useState<string | null>(null);

    const { category } = useParams<{ category: string }>();

    const getEbooks = useCallback(async () => {
        try {
            setLoadingEbooks(true);
            setError(null);
            
            const ebooksService = new EbooksService();
            const response = await ebooksService.list();

            if (response.error) {
                throw new Error(response.error);
            }

            if (response.next) {
                setEbooksNextPage(response.next.split("=")[1]);
                setViewMoreDisabled(false);
                setLastPage(false);
            } else {
                setViewMoreDisabled(true);
                setLastPage(true);
            }

            setEbooks(response.results || []);
            setEbooksCount(response.count || 0);
        } catch (error) {
            console.error('Error loading ebooks:', error);
            setError('Erro ao carregar e-books. Tente novamente.');
            setEbooks([]);
            setEbooksCount(0);
        } finally {
            setLoadingEbooks(false);
        }
    }, []);

    const loadMoreEbooks = useCallback(async () => {
        try {
            setViewMoreDisabled(true);
            setError(null);

            const ebooksService = new EbooksService(ebooksNextPage);
            const response = await ebooksService.list();

            if (response.error) {
                throw new Error(response.error);
            }

            setEbooks(prevEbooks => [...prevEbooks, ...(response.results || [])]);

            if (response.next) {
                setEbooksNextPage(response.next.split("=")[1]);
                setViewMoreDisabled(false);
            } else {
                setLastPage(true);
                setViewMoreDisabled(true);
            }
        } catch (error) {
            console.error('Error loading more ebooks:', error);
            setError('Erro ao carregar mais e-books. Tente novamente.');
            setViewMoreDisabled(false);
        }
    }, [ebooksNextPage]);

    const truncateString = useCallback((txt: string | undefined, charCount: number) => {
        return sanitizeAndTruncate(txt || '', charCount);
    }, []);

    const getEbooksByCategory = useCallback(async (category: string) => {
        try {
            setLoadingEbooks(true);
            setError(null);
            
            const ebooksService = new EbooksService();
            const response = await ebooksService.get_by_category_slug(category);

            if (response.error) {
                throw new Error(response.error);
            }

            if (response.next) {
                setEbooksNextPage(response.next.split("=")[1]);
                setViewMoreDisabled(false);
                setLastPage(false);
            } else {
                setViewMoreDisabled(true);
                setLastPage(true);
            }
            
            setEbooks(response.results || []);
            setEbooksCount(response.count || 0);
        } catch (error) {
            console.error('Error loading ebooks by category:', error);
            setError('Erro ao carregar e-books da categoria. Tente novamente.');
            setEbooks([]);
            setEbooksCount(0);
        } finally {
            setLoadingEbooks(false);
        }
    }, []);

    const getCategories = useCallback(async () => {
        try {
            const ebooksCategoriesService = new EbooksCategoriesService();
            const response = await ebooksCategoriesService.list();
            setEbookCategories(response || []);
        } catch (error) {
            console.error('Error loading ebook categories:', error);
            setEbookCategories([]);
        }
    }, []);


    useEffect(() => {
        window.scrollTo(0, 0);
        if (category) {
            getEbooksByCategory(category);
        } else {
            getEbooks();
        }
        getCategories();
        // eslint-disable-next-line
    }, [category]);

    return (
        <PageContainer pageTitle="E-books" showSearch={false} showBreadcrumb pageTitleHighlightText={ebooksCount}>
            <FilterTitle>Categorias:</FilterTitle>
            <CategoriesContainer>
                {ebookCategories.map((ebookCategory: IEbookCategory, index: number) => (
                    <React.Fragment key={index}>
                        {ebookCategory.slug === category ? (
                            <Tag to="/ebooks" title="Remover Filtro" className="active">
                                {ebookCategory.nome}
                            </Tag>
                        ) : (
                            <Tag to={`/ebooks/categoria/${ebookCategory.slug}`} title={ebookCategory.descricao}>
                                {ebookCategory.nome}
                            </Tag>
                        )}
                    </React.Fragment>
                ))}
            </CategoriesContainer>
            <GreyContainer>
                <InnerContentContainer>
                    <HomePageFeatureSlider location="EBOOKS" />
                    <EbooksContainer {...props}>
                        <Box
                            width="100%"
                            display="grid"
                            gridTemplateColumns={{
                                xs: "100%",
                                sm: "100%",
                                md: "24% 24% 24% 24%",
                            }}
                            rowGap="15px"
                            columnGap="1%">
                            {loadingEbooks ? (
                                <>
                                    <CourseCardLoading /> <CourseCardLoading />
                                    <CourseCardLoading /> <CourseCardLoading />
                                </>
                            ) : error ? (
                                <div style={{ 
                                    gridColumn: '1 / -1', 
                                    textAlign: 'center', 
                                    padding: '40px 20px',
                                    color: '#666'
                                }}>
                                    <p>{error}</p>
                                    <Button 
                                        variant="outlined" 
                                        onClick={() => category ? getEbooksByCategory(category) : getEbooks()}
                                    >
                                        Tentar novamente
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    {ebooks.map((ebook: IEbook) => (
                                        <SquareCardLink
                                            key={ebook.id}
                                                downloadID={ebook.id}
                                                variant="download"
                                                href={ebook.arquivo}
                                                imageurl={ebook.imagem || FALLBACK_IMAGES.EBOOK}
                                                title={ebook.titulo}
                                                description={sanitizeHtml(ebook.descricao)}
                                                excerpt={truncateString(ebook.descricao, 100)}
                                                meta={ebook.data_publicacao}
                                                category={ebook.categoria}
                                                categoryRoute="/ebooks/categoria"
                                                contentType="ebook"
                                                isModal={ebook.descricao ? true : false}></SquareCardLink>
                                    ))}
                                </>
                            )}
                        </Box>
                        {!loadingEbooks && (
                            <ViewMoreWrapper>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={viewMoreDisabled}
                                    onClick={() => {
                                        loadMoreEbooks();
                                    }}>
                                    Ver mais E-books
                                </Button>
                                {viewMoreDisabled && !lastPage ? <img className="loadingGif" src={loadingGif} alt="loading" /> : ""}
                            </ViewMoreWrapper>
                        )}
                    </EbooksContainer>
                </InnerContentContainer>
            </GreyContainer>
        </PageContainer>
    );
};

export { Ebooks };